import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  ExternalLink,
  Play,
  Users,
  TrendingUp,
  Award,
  ArrowRight,
  BookOpen,
  BarChart3,
  Smartphone,
  Globe,
  Shield,
  Zap,
  Star,
  ChevronRight,
} from "lucide-react";

const PortfolioContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.gradients.background};
  padding-top: 6rem;
  overflow-x: hidden;
  width: 100%;
  position: relative;
`;

const HeroSection = styled.section`
  padding: 4rem 2rem 6rem;
  background: ${({ theme }) => theme.colors.surface.primary};
  text-align: center;
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 3rem 1.5rem 5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 2.5rem 1rem 4rem;
  }
`;

const HeroContent = styled(motion.div)`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1.5rem;
  line-height: 1.2;
`;

const HeroSubtitle = styled(motion.p)`
  font-size: ${({ theme }) => theme.typography.sizes.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const FilterTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 4rem;
  flex-wrap: wrap;
`;

const FilterTab = styled(motion.button)<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  background: ${({ active, theme }) =>
    active ? theme.colors.gradients.primary : theme.colors.surface.primary};
  color: ${({ active, theme }) =>
    active ? theme.colors.text.inverse : theme.colors.text.secondary};
  border: 1px solid
    ${({ active, theme }) =>
      active ? "transparent" : theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};

  &:hover {
    background: ${({ active, theme }) =>
      active ? theme.colors.gradients.primary : theme.colors.surface.secondary};
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }
`;

const CaseStudiesSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.gradients.subtle};
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const SectionContainer = styled.div`
  max-width: ${({ theme }) => theme.container.xl};
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CaseStudyGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 4rem;
  max-width: ${({ theme }) => theme.container.xl};
  margin-left: auto;
  margin-right: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    gap: 2rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: 1.5rem;
  }
`;

const CaseStudyCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface.primary};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.card};
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  width: 100%;
  max-width: 400px;
  min-width: 350px;

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.elevated};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: 300px;
    max-width: 100%;
  }
`;

const CaseStudyImage = styled.div<{ bgColor: string }>`
  height: 200px;
  background: ${({ bgColor }) => bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const PlayButton = styled(motion.button)`
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.accent.primary};
  box-shadow: ${({ theme }) => theme.shadows.lg};

  &:hover {
    background: white;
    transform: scale(1.1);
  }
`;

const CaseStudyContent = styled.div`
  padding: 2rem;
`;

const CaseStudyTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.sizes.xl};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const CaseStudyClient = styled.p`
  color: ${({ theme }) => theme.colors.accent.primary};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  margin-bottom: 1rem;
`;

const CaseStudyDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const CaseStudyMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Metric = styled.div`
  text-align: center;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.gradients.subtle};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const MetricValue = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.xl};
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.accent.primary};
`;

const MetricLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 0.25rem;
`;

const CaseStudyTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Tag = styled.span`
  padding: 0.25rem 0.75rem;
  background: ${({ theme }) => theme.colors.surface.secondary};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
`;

const ViewCaseStudyButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.accent.primary};
  border: 1px solid ${({ theme }) => theme.colors.accent.primary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};

  &:hover {
    background: ${({ theme }) => theme.colors.accent.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
    transform: translateX(4px);
  }
`;

const TestimonialsSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.surface.primary};
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const TestimonialGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
  max-width: ${({ theme }) => theme.container.xl};
  margin: 0 auto;
`;

const TestimonialCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.gradients.subtle};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  width: 100%;
  max-width: 400px;
  min-width: 320px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: 280px;
    max-width: 100%;
  }
`;

const TestimonialText = styled.blockquote`
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-style: italic;

  &::before {
    content: '"';
    font-size: 2rem;
    color: ${({ theme }) => theme.colors.accent.primary};
    font-weight: bold;
  }

  &::after {
    content: '"';
    font-size: 2rem;
    color: ${({ theme }) => theme.colors.accent.primary};
    font-weight: bold;
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.colors.gradients.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.inverse};
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  font-size: ${({ theme }) => theme.typography.sizes.lg};
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.div`
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AuthorTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

const StatsSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.gradients.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  text-align: center;
`;

const StatsGrid = styled.div`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 3rem;
`;

const StatCard = styled(motion.div)`
  text-align: center;
`;

const StatNumber = styled(motion.div)`
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  opacity: 0.9;
`;

const Portfolio: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState("all");

  const filters = [
    { id: "all", label: "All Projects" },
    { id: "k12", label: "K-12 Platforms" },
    { id: "analytics", label: "Data Analytics" },
    { id: "mobile", label: "Mobile Apps" },
    { id: "integration", label: "System Integration" },
  ];

  const caseStudies = [
    {
      id: "riverside-unified",
      title: "Student Engagement Revolution",
      client: "Riverside Unified School District",
      description:
        "Transformed student data into an engaging social media-style platform that increased daily active users by 340% and improved academic engagement scores.",
      metrics: [
        { value: "340%", label: "User Growth" },
        { value: "89%", label: "Engagement" },
        { value: "25%", label: "Grade Improvement" },
      ],
      tags: ["K-12", "Social Learning", "Mobile", "Analytics"],
      bgColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      category: "k12",
    },
    {
      id: "metro-academy",
      title: "Real-Time Learning Analytics",
      client: "Metro Academy Charter Schools",
      description:
        "Built comprehensive analytics dashboard that provides teachers with instant insights into student performance, leading to 40% faster intervention times.",
      metrics: [
        { value: "40%", label: "Faster Response" },
        { value: "95%", label: "Teacher Adoption" },
        { value: "18%", label: "Score Increase" },
      ],
      tags: ["Analytics", "Dashboard", "Real-time", "Teacher Tools"],
      bgColor: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      category: "analytics",
    },
    {
      id: "citywide-mobile",
      title: "Mobile-First Learning Hub",
      client: "Citywide Education Network",
      description:
        "Developed cross-platform mobile application serving 12,000+ students with offline capabilities and seamless parent communication features.",
      metrics: [
        { value: "12K+", label: "Active Users" },
        { value: "4.8★", label: "App Rating" },
        { value: "99.9%", label: "Uptime" },
      ],
      tags: ["Mobile", "Cross-platform", "Offline", "Parent Portal"],
      bgColor: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      category: "mobile",
    },
    {
      id: "state-integration",
      title: "Statewide Data Integration",
      client: "State Department of Education",
      description:
        "Integrated 200+ school districts into unified reporting system, ensuring FERPA compliance while streamlining state assessment workflows.",
      metrics: [
        { value: "200+", label: "Districts" },
        { value: "100%", label: "FERPA Compliant" },
        { value: "60%", label: "Time Saved" },
      ],
      tags: ["Integration", "FERPA", "Compliance", "State-level"],
      bgColor: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      category: "integration",
    },
  ];

  const testimonials = [
    {
      text: "Swintek transformed our district's relationship with data. Students who never engaged with their academic progress are now checking their profiles daily and setting learning goals.",
      author: "Dr. Sarah Martinez",
      title: "Superintendent, Riverside Unified",
      initials: "SM",
    },
    {
      text: "The analytics platform has revolutionized how our teachers identify and support struggling students. Response times have improved dramatically.",
      author: "James Chen",
      title: "CTO, Metro Academy",
      initials: "JC",
    },
    {
      text: "Finally, a mobile platform that students actually want to use. The engagement metrics speak for themselves - this is the future of educational technology.",
      author: "Maria Rodriguez",
      title: "Technology Director, Citywide Education",
      initials: "MR",
    },
  ];

  const stats = [
    { number: "50+", label: "School Districts Served" },
    { number: "100K+", label: "Students Impacted" },
    { number: "95%", label: "Client Satisfaction" },
    { number: "24/7", label: "Support Available" },
  ];

  const filteredCaseStudies =
    activeFilter === "all"
      ? caseStudies
      : caseStudies.filter((study) => study.category === activeFilter);

  return (
    <PortfolioContainer>
      <HeroSection>
        <HeroContent
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Transforming Districts Nationwide
          </HeroTitle>

          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Explore our portfolio of successful educational technology
            implementations that have revolutionized how students learn and
            engage.
          </HeroSubtitle>

          <FilterTabs>
            {filters.map((filter) => (
              <FilterTab
                key={filter.id}
                active={activeFilter === filter.id}
                onClick={() => setActiveFilter(filter.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {filter.label}
              </FilterTab>
            ))}
          </FilterTabs>
        </HeroContent>
      </HeroSection>

      <CaseStudiesSection>
        <SectionContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Case Studies
          </SectionTitle>

          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Real results from real partnerships with educational institutions
          </SectionSubtitle>

          <CaseStudyGrid>
            {filteredCaseStudies.map((study, index) => (
              <CaseStudyCard
                key={study.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                layout
              >
                <CaseStudyImage bgColor={study.bgColor}>
                  <PlayButton
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Play size={32} fill="currentColor" />
                  </PlayButton>
                </CaseStudyImage>

                <CaseStudyContent>
                  <CaseStudyTitle>{study.title}</CaseStudyTitle>
                  <CaseStudyClient>{study.client}</CaseStudyClient>
                  <CaseStudyDescription>
                    {study.description}
                  </CaseStudyDescription>

                  <CaseStudyMetrics>
                    {study.metrics.map((metric, idx) => (
                      <Metric key={idx}>
                        <MetricValue>{metric.value}</MetricValue>
                        <MetricLabel>{metric.label}</MetricLabel>
                      </Metric>
                    ))}
                  </CaseStudyMetrics>

                  <CaseStudyTags>
                    {study.tags.map((tag, idx) => (
                      <Tag key={idx}>{tag}</Tag>
                    ))}
                  </CaseStudyTags>

                  <ViewCaseStudyButton
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    View Full Case Study
                    <ChevronRight size={16} />
                  </ViewCaseStudyButton>
                </CaseStudyContent>
              </CaseStudyCard>
            ))}
          </CaseStudyGrid>
        </SectionContainer>
      </CaseStudiesSection>

      <TestimonialsSection>
        <SectionContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            What Our Clients Say
          </SectionTitle>

          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Hear from educational leaders who've transformed their districts
            with Swintek
          </SectionSubtitle>

          <TestimonialGrid>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <TestimonialText>{testimonial.text}</TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar>{testimonial.initials}</AuthorAvatar>
                  <AuthorInfo>
                    <AuthorName>{testimonial.author}</AuthorName>
                    <AuthorTitle>{testimonial.title}</AuthorTitle>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
          </TestimonialGrid>
        </SectionContainer>
      </TestimonialsSection>

      <StatsSection>
        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <StatNumber
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{
                  duration: 0.8,
                  delay: index * 0.1 + 0.3,
                  type: "spring",
                  stiffness: 100,
                }}
                viewport={{ once: true }}
              >
                {stat.number}
              </StatNumber>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>
      </StatsSection>
    </PortfolioContainer>
  );
};

export default Portfolio;
